import React, { useEffect, useState } from 'react';
import { usePackageProduct } from '@jetshop/core/hooks/PackageProducts';
import addMultipleToCartMutation from '../../Cart/addMultipleToCart.gql';
import cartQuery from '../../Cart/CartQuery.gql';
import { PackageProductItem } from './PackageProductItem';
import {
  default as Button,
  default as ButtonWithLoading,
} from '../../ui/Button';
import { styled } from 'linaria/react';
import { useIntl } from '@jetshop/intl';
import { Price } from '@jetshop/ui/Price';
import packageProductQuery from './PackageProductQuery.gql';
import { useNotification } from '@jetshop/core/components/Notifications';
import ProductToastWrapper from '../AddToCart/ProductToast';

const PackageProductWrapper = styled('div')`
  padding: 12px;
  background-color: white;
  display: flex;
  flex-direction: column;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  margin-top: 24px;
  margin-bottom: 24px;
  width: 100%;

  .error {
    margin-top: 1em;
    background: #faf3f3;
    padding: 1em;

    label {
      color: #ca0c0c;
    }
  }

  .error button {
    margin-top: 1em;
  }

  .package-product-items-wrapper {
    display: flex;
    flex-direction: column;
  }

  .package-product-price-wrapper {
    font-size: 1.125rem;
    margin-bottom: 0.75em;

    .discount,
    .total {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }

    .discount {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      color: #5f5f5f;
      margin-bottom: 0.5rem;

      .price {
        font-size: 1.25rem;
        margin-top: 0.5rem;
      }
    }

    .total {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      font-weight: 600;
      align-items: center;

      .package-price {
        margin-top: 0.5rem;
        font-size: 1.5rem;
      }
    }
  }

  .package-product-loading {
    padding: 12px;
    color: #666;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const ButtonWrapper = styled('div')`
  width: 100%;
  &.not-buyable {
    opacity: 0.2;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 2;
      display: block;
    }
  }

  button {
    width: 100%;
    max-width: 100%;
    border-radius: 50px;
    position: relative;

    svg {
      width: 20px;
      height: 20px;
      position: absolute;
      right: 1rem;

      circle.path {
        stroke: white;
      }
    }
  }
`;

export function PackageProduct({ product }) {
  const {
    addPackageToCart,
    status,
    price,
    error,
    retry,
    updatePackageItem,
    packageProduct,
    selectedvariation,
  } = usePackageProduct({
    product,
    packageProductQuery,
  });

  const hidePrice = product?.hidePrice;

  // validation is not enabled until the 'add to cart' button is pressed once
  const [validationEnabled, enableValidation] = React.useReducer(
    () => true,
    false
  );

  // TOTAL PREVIOUS PRICE CACLULATION
  const [prevPrices, setPrevPrices] = useState([]);
  const setPrevPriceAtIndex = (index, prevPrice, qty) => {
    let newPrevPrices = [...prevPrices];
    if (qty && qty > 1) {
      prevPrice = addPriceToTotal(prevPrice, qty);
    }
    if (prevPrice && prevPrice.incVat !== newPrevPrices[index]?.incVat) {
      newPrevPrices[index] = prevPrice;
      setPrevPrices(newPrevPrices);
    }
  };

  const t = useIntl();

  const buyable = status === 'buyable';

  function add() {
    addPackageToCart({
      addMultipleToCartMutation,
      cartQuery,
    });
  }

  const [notify] = useNotification();

  useEffect(() => {
    if (status === 'added') {
      notify(<ProductToastWrapper product={product} error={error} />);
    }
  }, [notify, product.name, status, error, product]);

  if (status === 'loading') {
    return <LoadingState />;
  }

  return (
    <>
      <PackageProductWrapper>
        <div className="package-product-items-wrapper">
          {packageProduct.package.items.map((item, idx) => {
            return (
              <PackageProductItem
                item={item}
                key={item.product.id}
                index={idx}
                updatePackageItem={updatePackageItem}
                validationEnabled={validationEnabled}
                price={price.items[idx]}
                fetchingPrice={status === 'fetching_price'}
                setPrevPriceAtIndex={setPrevPriceAtIndex}
              />
            );
          })}
        </div>

        <PackagePrice
          data={price}
          fetching={status === 'fetching_price'}
          prevPrices={prevPrices}
        />
        <ButtonWrapper className={buyable ? 'buyable' : 'not-buyable'}>
          <Button
            disabled={validationEnabled && !buyable}
            loading={status === 'fetching_price'}
            loadingText={t('Updating price... ')}
            onClick={buyable ? add : enableValidation}
          >
            {status === 'added' ? t('Successfully added!') : t('Add to cart')}
          </Button>
        </ButtonWrapper>
        {status === 'error' && <Error error={error} retry={retry} />}
      </PackageProductWrapper>
    </>
  );
}

function LoadingState() {
  const t = useIntl();
  return (
    <PackageProductWrapper>
      <div className="package-product-loading">
        {t('Loading package details...')}
      </div>
    </PackageProductWrapper>
  );
}

function addPriceToTotal(priceToAdd, qty, oldTotal) {
  let newTotal = oldTotal
    ? oldTotal
    : {
        incVat: 0,
        exVat: 0,
        vat: 0,
        __typename: 'Price',
      };

  qty = qty ? qty : 1;

  if (priceToAdd) {
    newTotal.incVat = newTotal.incVat + priceToAdd.incVat * qty;
    newTotal.exVat = newTotal.exVat + priceToAdd.exVat * qty;
    newTotal.vat = newTotal.vat + priceToAdd.vat * qty;
  }

  return newTotal;
}

function PackagePrice({ data, fetching, prevPrices }) {
  const t = useIntl();

  // CALCULATE TOTAL MAXIMUM PREVIOUS PRICE
  let totalPrevPrice = null;
  prevPrices.forEach((prevPrice) => {
    totalPrevPrice = addPriceToTotal(prevPrice, 1, totalPrevPrice);
  });

  return (
    <div
      className="package-product-price-wrapper"
      style={{
        opacity: fetching ? 0.3 : 1,
      }}
    >
      {data.discountValue?.incVat > 0 && (
        <div className="discount">
          <p className="package-price-label">{t('Package Discount')}</p>
          <Price
            negative
            price={data.discountValue}
            className="package-price"
          ></Price>
        </div>
      )}
      <div className="total">
        <p className="package-price-label">{t('Total')}</p>
        <Price
          price={data.price}
          previousPrice={totalPrevPrice}
          className="package-price"
        ></Price>
      </div>
    </div>
  );
}

function Error({ error, retry }) {
  const t = useIntl();

  // TODO: API will be changed to allow a `abortOnError` or similar variable
  // for addMultipleToCart. Currently it's possible to end up in a state where
  // some items in the package failed and some succeeded. After the change,
  // complete failure will be the only case we need to handle.
  return (
    <div className="error">
      <label>
        {t('Something went wrong when { placeholder }', {
          placeholder:
            error.fromState === 'fetchingPrice'
              ? ' fetching price. This item cannot be added to the cart at this time'
              : ' adding to cart',
        })}
        .
      </label>
      {error.messages.map((err) => (
        <div>{err}</div>
      ))}
      <ButtonWithLoading onClick={retry}>{t('Retry')}</ButtonWithLoading>
    </div>
  );
}
